/**
 * OnboardingChecklist — displays and manages vendor onboarding checklist
 * on the VendorDetail page.
 *
 * Shows checklist items with status indicators, completion tracking,
 * document links, and actions to mark items complete or waived.
 * Includes a progress bar showing completion status.
 *
 * Requirements: 22.1, 22.3, 22.4
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  onboardingService,
  type UpdateChecklistItemInput,
} from '@/services/onboardingService';
import { StatusBadge, type BadgeVariant } from '@/components/ui/StatusBadge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { Modal } from '@/components/ui/Modal';
import type { ChecklistItem } from '@/types';
import { ChecklistItemStatus } from '@/types';

// ─── Constants ─────────────────────────────────────────────────────────

const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  Pending: 'gray',
  Complete: 'green',
  Waived: 'yellow',
};

const STATUS_ICONS: Record<string, string> = {
  Pending: '○',
  Complete: '✓',
  Waived: '—',
};

const STATUS_COLORS: Record<string, string> = {
  Pending: '#94a3b8',
  Complete: '#16a34a',
  Waived: '#ca8a04',
};

const EGNYTE_BASE_URL = 'https://bakerdist.egnyte.com/navigate/file/';

// ─── Props ─────────────────────────────────────────────────────────────

interface OnboardingChecklistProps {
  vendorId: number;
  canWrite: boolean;
}

// ─── Component ─────────────────────────────────────────────────────────

export function OnboardingChecklist({ vendorId, canWrite }: OnboardingChecklistProps) {
  const queryClient = useQueryClient();

  const [waiverItemId, setWaiverItemId] = useState<number | null>(null);
  const [waiverJustification, setWaiverJustification] = useState('');
  const [waiverError, setWaiverError] = useState<string | null>(null);

  // Fetch checklist
  const {
    data: checklist,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['onboarding-checklist', vendorId],
    queryFn: () => onboardingService.getChecklistForVendor(vendorId),
    enabled: vendorId > 0,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['onboarding-checklist', vendorId] });
    queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });
  };

  // Generate checklist mutation
  const generateMutation = useMutation({
    mutationFn: () => onboardingService.generateChecklist(vendorId),
    onSuccess: () => invalidate(),
  });

  // Update item mutation
  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, input }: { itemId: number; input: UpdateChecklistItemInput }) =>
      onboardingService.updateChecklistItem(itemId, input),
    onSuccess: () => invalidate(),
  });

  function handleComplete(itemId: number) {
    updateItemMutation.mutate({
      itemId,
      input: { status: 'Complete' },
    });
  }

  function openWaiverDialog(itemId: number) {
    setWaiverItemId(itemId);
    setWaiverJustification('');
    setWaiverError(null);
  }

  function closeWaiverDialog() {
    setWaiverItemId(null);
    setWaiverJustification('');
    setWaiverError(null);
  }

  function handleWaiverSubmit() {
    if (!waiverJustification.trim()) {
      setWaiverError('Waiver justification is required.');
      return;
    }
    if (waiverItemId === null) return;

    updateItemMutation.mutate(
      {
        itemId: waiverItemId,
        input: {
          status: 'Waived',
          waiverJustification: waiverJustification.trim(),
        },
      },
      { onSuccess: () => closeWaiverDialog() },
    );
  }

  // ─── Loading / Error States ────────────────────────────────────────

  if (isLoading) {
    return (
      <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.75rem' }}>
          Onboarding Checklist
        </h2>
        <LoadingSpinner message="Loading onboarding checklist…" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.75rem' }}>
          Onboarding Checklist
        </h2>
        <ErrorMessage
          message={(error as Error)?.message ?? 'Failed to load onboarding checklist.'}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  // ─── No Checklist — Generate Button ────────────────────────────────

  if (!checklist) {
    return (
      <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 0.75rem' }}>
          Onboarding Checklist
        </h2>
        <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: '0 0 0.75rem' }}>
          No onboarding checklist has been generated for this vendor.
        </p>
        {canWrite && (
          <button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
            style={{
              padding: '0.4rem 0.75rem',
              borderRadius: '4px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              cursor: 'pointer',
              fontSize: '0.8rem',
            }}
          >
            {generateMutation.isPending ? 'Generating…' : 'Generate Checklist'}
          </button>
        )}
        {generateMutation.isError && (
          <div
            style={{
              marginTop: '0.5rem',
              padding: '0.5rem',
              backgroundColor: '#fef2f2',
              borderRadius: '4px',
              color: '#991b1b',
              fontSize: '0.8rem',
            }}
          >
            {(generateMutation.error as Error)?.message ?? 'Failed to generate checklist.'}
          </div>
        )}
      </div>
    );
  }

  // ─── Checklist with Items ──────────────────────────────────────────

  const items: ChecklistItem[] = checklist.items ?? [];
  const completedCount = items.filter(
    (i) => i.status === ChecklistItemStatus.Complete || i.status === ChecklistItemStatus.Waived,
  ).length;
  const totalCount = items.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.75rem',
        }}
      >
        <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>
          Onboarding Checklist
        </h2>
        <span style={{ fontSize: '0.8rem', color: '#475569' }}>
          {completedCount} of {totalCount} items complete
        </span>
      </div>

      {/* Progress Bar */}
      <div
        style={{
          width: '100%',
          height: '8px',
          backgroundColor: '#e2e8f0',
          borderRadius: '4px',
          marginBottom: '1rem',
          overflow: 'hidden',
        }}
        role="progressbar"
        aria-valuenow={completedCount}
        aria-valuemin={0}
        aria-valuemax={totalCount}
        aria-label={`${completedCount} of ${totalCount} onboarding items complete`}
      >
        <div
          style={{
            width: `${progressPercent}%`,
            height: '100%',
            backgroundColor: progressPercent === 100 ? '#16a34a' : '#c8102e',
            borderRadius: '4px',
            transition: 'width 0.3s ease',
          }}
        />
      </div>

      {/* Checklist Items Table */}
      {items.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No checklist items.</p>
      ) : (
        <table
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}
          role="table"
          aria-label="Onboarding checklist items"
        >
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
              <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569', width: '2rem' }}>
                {/* Status icon */}
              </th>
              <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>
                Task
              </th>
              <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>
                Status
              </th>
              <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>
                Completed By
              </th>
              <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#475569' }}>
                Date
              </th>
              <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: '#475569' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <ChecklistItemRow
                key={item.checklistItemId}
                item={item}
                canWrite={canWrite}
                isPending={updateItemMutation.isPending}
                onComplete={() => handleComplete(item.checklistItemId)}
                onWaive={() => openWaiverDialog(item.checklistItemId)}
              />
            ))}
          </tbody>
        </table>
      )}

      {/* Waiver Justification Modal */}
      <Modal
        isOpen={waiverItemId !== null}
        onClose={closeWaiverDialog}
        title="Waive Checklist Item"
        footer={
          <>
            <button
              onClick={closeWaiverDialog}
              style={{
                padding: '0.4rem 1rem',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                backgroundColor: '#fff',
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleWaiverSubmit}
              disabled={updateItemMutation.isPending}
              style={{
                padding: '0.4rem 1rem',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: '#ca8a04',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              {updateItemMutation.isPending ? 'Saving…' : 'Waive Item'}
            </button>
          </>
        }
      >
        <p style={{ margin: '0 0 0.75rem', color: '#334155', fontSize: '0.875rem' }}>
          Please provide a justification for waiving this checklist item.
        </p>
        {waiverError && (
          <div
            style={{
              marginBottom: '0.75rem',
              padding: '0.5rem',
              backgroundColor: '#fef2f2',
              borderRadius: '4px',
              color: '#991b1b',
              fontSize: '0.8rem',
            }}
          >
            {waiverError}
          </div>
        )}
        <textarea
          value={waiverJustification}
          onChange={(e) => setWaiverJustification(e.target.value)}
          placeholder="Enter waiver justification…"
          rows={3}
          style={{
            width: '100%',
            padding: '0.5rem',
            borderRadius: '4px',
            border: '1px solid #d1d5db',
            fontSize: '0.875rem',
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
        />
      </Modal>
    </div>
  );
}

// ─── Row Component ─────────────────────────────────────────────────────

interface ChecklistItemRowProps {
  item: ChecklistItem;
  canWrite: boolean;
  isPending: boolean;
  onComplete: () => void;
  onWaive: () => void;
}

function ChecklistItemRow({ item, canWrite, isPending, onComplete, onWaive }: ChecklistItemRowProps) {
  const isDone = item.status === ChecklistItemStatus.Complete || item.status === ChecklistItemStatus.Waived;

  return (
    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
      {/* Status Icon */}
      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
        <span
          style={{
            color: STATUS_COLORS[item.status] ?? '#94a3b8',
            fontSize: '1rem',
            fontWeight: 600,
          }}
          aria-hidden="true"
        >
          {STATUS_ICONS[item.status] ?? '○'}
        </span>
      </td>

      {/* Task Description */}
      <td style={{ padding: '0.4rem 0.5rem' }}>
        <div>
          <span style={{ fontWeight: 500 }}>{item.taskDescription}</span>
          {item.status === ChecklistItemStatus.Waived && item.waiverJustification && (
            <div style={{ fontSize: '0.75rem', color: '#ca8a04', marginTop: '0.15rem' }}>
              Waiver: {item.waiverJustification}
            </div>
          )}
          {item.egnyteFilePath && (
            <div style={{ marginTop: '0.15rem' }}>
              <a
                href={`${EGNYTE_BASE_URL}${encodeURIComponent(item.egnyteFilePath)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '0.75rem', color: '#c8102e', textDecoration: 'none' }}
              >
                📄 View Document
              </a>
            </div>
          )}
        </div>
      </td>

      {/* Status Badge */}
      <td style={{ padding: '0.4rem 0.5rem' }}>
        <StatusBadge
          label={item.status}
          variant={STATUS_VARIANTS[item.status] ?? 'gray'}
        />
      </td>

      {/* Completed By */}
      <td style={{ padding: '0.4rem 0.5rem' }}>
        {item.completedBy ?? '—'}
      </td>

      {/* Completed Date */}
      <td style={{ padding: '0.4rem 0.5rem' }}>
        {item.completedDate
          ? new Date(item.completedDate).toLocaleDateString()
          : '—'}
      </td>

      {/* Actions */}
      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>
        {canWrite && !isDone && (
          <span style={{ display: 'inline-flex', gap: '0.25rem' }}>
            <button
              onClick={onComplete}
              disabled={isPending}
              style={{
                padding: '0.2rem 0.5rem',
                borderRadius: '4px',
                border: '1px solid #bbf7d0',
                backgroundColor: '#f0fdf4',
                color: '#16a34a',
                cursor: 'pointer',
                fontSize: '0.7rem',
              }}
            >
              Complete
            </button>
            <button
              onClick={onWaive}
              disabled={isPending}
              style={{
                padding: '0.2rem 0.5rem',
                borderRadius: '4px',
                border: '1px solid #fde68a',
                backgroundColor: '#fefce8',
                color: '#ca8a04',
                cursor: 'pointer',
                fontSize: '0.7rem',
              }}
            >
              Waive
            </button>
          </span>
        )}
      </td>
    </tr>
  );
}
